import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Utilitários de tema. Equivalentes aos do pacote `minimal-shared` do Minimal UI (MIT),
 * reimplementados aqui para não depender de um pacote cuja compatibilidade com o MUI 9
 * ninguém garante.
 */

type SxItem = boolean | Exclude<SxProps<Theme>, ReadonlyArray<unknown>>;

/**
 * Normaliza um `sx` recebido por props para ser espalhado depois dos estilos próprios do
 * componente: `sx={[{ ...próprios }, ...sxList(sx)]}`. É o padrão que a documentação do
 * MUI recomenda, só que tipado — `Array.isArray` sozinho vira `any[]` para o lint.
 */
export function sxList(sx?: SxProps<Theme>): readonly SxItem[] {
  if (sx === undefined) {
    return [];
  }

  return Array.isArray(sx) ? (sx as readonly SxItem[]) : [sx as SxItem];
}

/** "#0B84FF" → "11 132 255": o formato de canal que o MUI usa nas variáveis CSS. */
export function hexToChannel(hex: string): string {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);

  return `${r} ${g} ${b}`;
}

/** Uma cor com transparência a partir de um canal ou de uma variável CSS de canal. */
export function varAlpha(channel: string, opacity: number): string {
  return `rgba(${channel} / ${opacity})`;
}

type ChannelKeys<T> = { [K in keyof T & string as `${K}Channel`]: string };

/** Acrescenta a cada cor o canal correspondente (`main` → `mainChannel`). */
export function createPaletteChannel<T extends Record<string, string>>(
  colors: T,
): T & ChannelKeys<T> {
  const channels = Object.fromEntries(
    Object.entries(colors).map(([key, value]) => [`${key}Channel`, hexToChannel(value)]),
  ) as ChannelKeys<T>;

  return { ...colors, ...channels };
}

export function pxToRem(value: number): string {
  return `${value / 16}rem`;
}

export function setFont(fontName: string): string {
  return `"${fontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
}
